import 'material-symbols/outlined.css';
import { Box, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import useFetch from '../../api/useFetch';
import { helpVars } from './helpTheme';
import { toApiLocale } from './apiLocale';
import { HelpSidebar } from './HelpSidebar';

// Standalone help center (from the "ExoPaper Help Center" design) — opened in its
// own tab, persistent left sidebar, no default ExoPaper navigation.
export const DocumentationLayout = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const navigate = useNavigate();
  const location = useLocation();

  const locale = toApiLocale(i18n.language);
  const [navOpen, setNavOpen] = useState(false);
  const [role, setRole] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search] = useDebounce(searchInput, 300);
  const searchRef = useRef(null);

  const { apiData: topicsData } = useFetch(
    `documentation-topics?locale=${locale}`
  );
  const { apiData: entriesData } = useFetch(`documentation?locale=${locale}`);
  const { apiData: coursesData } = useFetch(
    `documentation-courses?locale=${locale}`
  );

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function handleSearchChange(value) {
    setSearchInput(value);
    // Searching always happens on the home view
    if (value && location.pathname !== '/documentation')
      navigate('/documentation');
  }

  const context = {
    locale,
    role,
    setRole,
    search,
    searchInput,
    setSearchInput: handleSearchChange,
    setNavOpen,
    topics: topicsData?.data,
    entries: entriesData?.data,
    courses: coursesData?.data,
  };

  return (
    <Box
      className="flex h-screen overflow-hidden"
      sx={{
        ...helpVars(mode),
        bgcolor: 'var(--bg)',
        color: 'var(--text)',
        fontSize: '14px',
        '& a': { color: 'var(--accent-strong)', textDecoration: 'none' },
        '& a:hover': { color: 'var(--accent)', textDecoration: 'underline' },
      }}
    >
      <HelpSidebar
        navOpen={navOpen}
        onClose={() => setNavOpen(false)}
        searchRef={searchRef}
        searchInput={searchInput}
        onSearchChange={handleSearchChange}
        role={role}
        setRole={setRole}
        topics={topicsData?.data}
        entries={entriesData?.data}
        courses={coursesData?.data}
      />
      {navOpen && (
        <Box
          className="fixed inset-0 z-30 md:hidden"
          sx={{ bgcolor: 'rgba(0,0,0,.35)' }}
          onClick={() => setNavOpen(false)}
        />
      )}
      <Outlet context={context} />
    </Box>
  );
};
